import { useEffect, useState, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, taskAPI, shareAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { TaskStatusIndicator } from '../components/TaskStatusIndicator';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Plus, FolderKanban, Loader2, Trash2, ChevronLeft, ChevronRight as ChevronRightIcon,
  Calendar, ClipboardList, Circle, CheckCircle2, ArrowLeft, Share2, Link2, Unlink
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatTaskDate } from '../lib/dateFormat';

const COLORS = ['#14b8a6', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const PRIORITY_DOT = { high: 'bg-rose-400', medium: 'bg-amber-400', low: 'bg-blue-400' };
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', waiting: 'Waiting', completed: 'Completed', cancelled: 'Cancelled' };
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ProjectsPage() {
  const { currentWorkspace } = useWorkspace();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', color: '#14b8a6' });
  const [saving, setSaving] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');
  const [calMonth, setCalMonth] = useState(new Date());
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchProjects = useCallback(() => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    projectAPI.list(currentWorkspace.id)
      .then(res => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const openProject = async (projectId) => {
    if (!currentWorkspace?.id) return;
    try {
      const res = await projectAPI.get(currentWorkspace.id, projectId);
      setActiveProject(res.data);
      setProjectTasks(res.data.tasks || []);
    } catch { toast.error('Failed to load project'); }
  };

  useEffect(() => {
    const pid = searchParams.get('id');
    if (pid && currentWorkspace?.id) openProject(pid);
  }, [searchParams, currentWorkspace?.id]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const res = await projectAPI.create({ workspace_id: currentWorkspace.id, ...form });
      toast.success('Project created');
      setShowCreate(false);
      setForm({ name: '', description: '', color: '#14b8a6' });
      fetchProjects();
      openProject(res.data.id);
    } catch { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await projectAPI.delete(currentWorkspace.id, id);
      toast.success('Project deleted');
      if (activeProject?.id === id) setActiveProject(null);
      fetchProjects();
    } catch { toast.error('Failed to delete'); }
  };

  // Calendar helpers
  const calYear = calMonth.getFullYear();
  const calMon = calMonth.getMonth();
  const firstDay = new Date(calYear, calMon, 1).getDay();
  const daysInMonth = new Date(calYear, calMon + 1, 0).getDate();
  const todayStr = new Date().toISOString().slice(0, 10);

  const getCalTasks = (day) => {
    const ds = `${calYear}-${String(calMon + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return projectTasks.filter(t => t.due_date === ds);
  };

  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);

  // If viewing a project
  if (activeProject) {
    const progress = activeProject.task_count > 0 ? Math.round(((activeProject.completed_count || 0) / (activeProject.task_count || 1)) * 100) : 0;
    return (
      <div className="h-full flex flex-col" data-testid="project-detail">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border/50 shrink-0">
          <button onClick={() => setActiveProject(null)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-3 w-3" /> All Projects
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${activeProject.color || '#14b8a6'}20` }}>
              <FolderKanban className="h-5 w-5" style={{ color: activeProject.color }} />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-display font-bold">{activeProject.name}</h1>
              {activeProject.description && <p className="text-xs text-muted-foreground mt-0.5">{activeProject.description}</p>}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span>{projectTasks.filter(t => t.status === 'completed').length}/{projectTasks.length} done</span>
              <Progress value={progress} className="w-24 h-1.5" />
              <span className="font-medium">{progress}%</span>
              <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1 border-border/30" data-testid="share-project-btn"
                onClick={async () => {
                  if (activeProject.is_shared) {
                    await shareAPI.revokeProjectLink(currentWorkspace.id, activeProject.id);
                    setActiveProject(prev => ({ ...prev, is_shared: false, share_token: '' }));
                    toast.success('Share link revoked');
                  } else {
                    const res = await shareAPI.createProjectLink(currentWorkspace.id, activeProject.id);
                    navigator.clipboard.writeText(res.data.share_url).catch(() => {});
                    setActiveProject(prev => ({ ...prev, is_shared: true, share_token: res.data.share_token }));
                    toast.success('Share link copied to clipboard');
                  }
                }}>
                {activeProject.is_shared ? <><Unlink className="h-3 w-3" /> Unshare</> : <><Share2 className="h-3 w-3" /> Share</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="px-6 border-b border-border/50">
            <TabsList className="bg-transparent h-10">
              <TabsTrigger value="tasks" className="text-xs gap-1.5"><ClipboardList className="h-3.5 w-3.5" /> Tasks</TabsTrigger>
              <TabsTrigger value="calendar" className="text-xs gap-1.5"><Calendar className="h-3.5 w-3.5" /> Calendar</TabsTrigger>
            </TabsList>
          </div>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="flex-1 overflow-y-auto p-6 mt-0">
            <div className="space-y-1">
              {projectTasks.length === 0 ? (
                <div className="text-center py-16">
                  <ClipboardList className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No tasks in this project yet</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/tasks/all?new=true')}>Create task</Button>
                </div>
              ) : projectTasks.map(task => {
                const isDone = task.status === 'completed' || task.status === 'cancelled';
                return (
                  <div key={task.id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => navigate(`/tasks/all?task=${task.id}`)}>
                    {isDone ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground/30 shrink-0" />}
                    <span className={`text-[13px] flex-1 truncate ${isDone ? 'line-through text-muted-foreground' : ''}`}>{task.title}</span>
                    {!isDone && <span className={`h-2 w-2 rounded-full ${PRIORITY_DOT[task.priority]}`} />}
                    {task.due_date && <span className="text-[11px] text-muted-foreground">{formatTaskDate(task.due_date)}</span>}
                    <TaskStatusIndicator status={task.status} size="xs" />
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="flex-1 overflow-y-auto p-6 mt-0">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalMonth(new Date(calYear, calMon - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-medium">{calMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalMonth(new Date(calYear, calMon + 1, 1))}><ChevronRightIcon className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-7 mb-1">
              {DAY_NAMES.map(d => <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground/50 py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calCells.map((day, i) => {
                if (!day) return <div key={`e-${i}`} className="min-h-[80px]" />;
                const ds = `${calYear}-${String(calMon + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayTasks = getCalTasks(day);
                const isToday = ds === todayStr;
                return (
                  <div key={day} className={`min-h-[80px] rounded-lg p-1.5 border transition-colors ${isToday ? 'border-primary/50 bg-primary/5' : 'border-transparent bg-secondary/10 hover:bg-secondary/20'}`}>
                    <span className={`text-[10px] font-semibold ${isToday ? 'text-primary' : 'text-muted-foreground/50'}`}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayTasks.slice(0, 3).map(t => (
                        <div key={t.id} className="flex items-center gap-1 p-0.5">
                          {t.status !== 'completed' && t.status !== 'cancelled' && <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${PRIORITY_DOT[t.priority]}`} />}
                          <span className="text-[9px] truncate">{t.title}</span>
                        </div>
                      ))}
                      {dayTasks.length > 3 && <span className="text-[8px] text-muted-foreground">+{dayTasks.length - 3}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Project list
  return (
    <div className="max-w-4xl mx-auto p-8 space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold tracking-tight">Projects</h1>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-project-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Project
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-32 bg-secondary/30 rounded-xl animate-pulse" />)}</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-24">
          <FolderKanban className="h-12 w-12 text-muted-foreground/20 mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No projects yet</p>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => setShowCreate(true)}>Create your first project</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map(p => {
            const progress = p.task_count > 0 ? Math.round((p.completed_count / p.task_count) * 100) : 0;
            return (
              <Card key={p.id} className="bg-card/60 backdrop-blur hover:border-border/60 transition-all cursor-pointer group hover:shadow-md" onClick={() => openProject(p.id)} data-testid={`project-card-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${p.color}20` }}>
                        <FolderKanban className="h-5 w-5" style={{ color: p.color }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{p.name}</p>
                        {p.description && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{p.description}</p>}
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 transition-opacity">
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{p.completed_count}/{p.task_count} tasks</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">New Project</DialogTitle>
            <DialogDescription>Create a project to organize related tasks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1"><Label>Name</Label><Input placeholder="Project name..." value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="project-name-input" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea placeholder="What is this project about?" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="min-h-[60px]" /></div>
            <div className="space-y-1">
              <Label>Color</Label>
              <div className="flex gap-2">
                {COLORS.map(c => (
                  <button key={c} className={`h-7 w-7 rounded-full transition-transform ${form.color === c ? 'ring-2 ring-offset-2 ring-offset-background scale-110' : 'hover:scale-105'}`} style={{ backgroundColor: c }} onClick={() => setForm(f => ({ ...f, color: c }))} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
